import { Component } from '@felicity/vision';
import './PresentationViewer.scss';

const
  /** Aspect ratio used until the real slide size is known */
  DEFAULT_RATIO = 9 / 16,

  /**
   * Replaces the `{0}`, `{1}`, … placeholders of a text
   * @param {string} text Text with placeholders
   * @param {...any} args Values of the placeholders
   * @returns {string} Formatted text
   */
  format = (text, ...args) => text.replace(/\{(\d+)\}/g, (match, i) => `${args[i] ?? match}`);

/**
 * Renders a PowerPoint presentation (.pptx) in the browser
 *
 * The presentation is downloaded as a whole and rendered client side by `pptx-preview`, which is
 * loaded on demand so that other screens do not pay for it.
 * Texts can be localized with `data-*-text` attributes, where `{0}` and `{1}` are placeholders.
 * @example
 * <flc-presentation-viewer data-src="/content/training/deck.pptx"></flc-presentation-viewer>
 */
export default class PresentationViewer extends Component {
  /**
   * URL of the presentation
   * @type {string}
   */
  src = null;

  /** Label of the full screen button */
  fullScreenText = 'Full screen';

  /** Download progress when the size is unknown; `{0}` is the downloaded size in MB */
  downloadingText = 'Downloading presentation… {0} MB';

  /** Download progress; `{0}` is the downloaded size, `{1}` is the total size in MB */
  downloadingOfText = 'Downloading presentation… {0} / {1} MB';

  /** Accessible label of the progress bar */
  downloadProgressText = 'Download progress';

  /** Shown while the slides are being rendered */
  preparingText = 'Preparing slides…';

  /** Shown when the presentation could not be displayed */
  failedText = 'The presentation could not be displayed. Please download it instead.';

  resizeAware = true;

  /** @type {import('pptx-preview/dist/previewer/PPTXPreviewer').PPTXPreviewer} */
  #previewer = null;

  /** @type {HTMLElement} */
  #stage;

  /** @type {HTMLElement} */
  #status;

  /** @type {AbortController} */
  #abort = new AbortController();

  _prepare() {
    super._prepare();
    this.classList.add('presentation-viewer');
    this.tabIndex = 0;

    this.#status = document.createElement('div');
    this.#status.className = 'presentation-viewer-status';

    this.#stage = document.createElement('div');
    this.#stage.className = 'presentation-viewer-stage';

    const fullscreen = document.createElement('button');
    fullscreen.type = 'button';
    fullscreen.className = 'btn btn-sm btn-outline-secondary presentation-viewer-fullscreen';
    fullscreen.textContent = this.fullScreenText;
    fullscreen.hidden = true;
    fullscreen.addEventListener('click', () => this.requestFullscreen?.());

    this.append(this.#status, this.#stage, fullscreen);

    this.addEventListener('keydown', (e) => this.#navigate(e));
    this.addEventListener('fullscreenchange', () => this.resize());

    this.#load()
      .then(() => {
        fullscreen.hidden = !document.fullscreenEnabled;
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error(error); // eslint-disable-line no-console
          this.#showStatus(this.failedText, 'danger');
        }
      });
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#previewer?.destroy();
  }

  /**
   * Fits the rendered slides to the available width
   */
  resize() {
    if (!this.#previewer?.pptx) {
      return;
    }

    const {
        pptx, htmlRender, wrapper, options
      } = this.#previewer,
      width = Math.floor(this.#stage.clientWidth),
      height = Math.floor(document.fullscreenElement === this
        ? Math.min(width * (pptx.height / pptx.width), window.innerHeight - 64)
        : width * (pptx.height / pptx.width)),
      // In full screen the height may be the limiting dimension, so scale the width accordingly
      fitWidth = Math.floor(Math.min(width, height * (pptx.width / pptx.height)));

    if (fitWidth <= 0 || (fitWidth === options.width && height === options.height)) {
      return;
    }

    // pptx-preview has no public API for changing the viewport, so its options are updated
    // and the current slide is rendered again with the new scale
    options.width = fitWidth;
    options.height = height;
    htmlRender.options.viewPort = { width: fitWidth, height };
    htmlRender._calcScaleAndRenderPort(); // eslint-disable-line no-underscore-dangle
    wrapper.style.width = `${fitWidth}px`;
    wrapper.style.height = `${height}px`;

    this.#previewer.renderSingleSlide(this.#previewer.currentIndex);
    this.#previewer.updatePagination();
  }

  async #load() {
    if (!this.src) {
      throw new Error('No presentation source has been specified');
    }

    // Start loading the renderer while the (potentially large) file is downloading
    const library = import('pptx-preview'),
      buffer = await this.#download(this.src),
      { init } = await library;

    this.#showStatus(this.preparingText);

    const width = Math.floor(this.#stage.clientWidth);
    this.#previewer = init(this.#stage, {
      width,
      height: Math.floor(width * DEFAULT_RATIO),
      mode: 'slide'
    });
    await this.#previewer.preview(buffer);

    this.#status.hidden = true;
    this.resize();
  }

  /**
   * Downloads the presentation while reporting its progress
   * @param {string} url URL of the presentation
   * @returns {Promise<ArrayBuffer>} Content of the presentation
   */
  async #download(url) {
    const response = await fetch(url, { signal: this.#abort.signal });
    if (!response.ok) {
      throw new Error(`Downloading ${url} failed with ${response.status}`);
    }

    const total = Number(response.headers.get('Content-Length')) || 0,
      reader = response.body.getReader(),
      chunks = [];
    let received = 0;

    for (;;) {
      // eslint-disable-next-line no-await-in-loop
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      chunks.push(value);
      received += value.length;
      this.#showProgress(received, total);
    }

    const buffer = new Uint8Array(received);
    chunks.reduce((offset, chunk) => {
      buffer.set(chunk, offset);
      return offset + chunk.length;
    }, 0);

    return buffer.buffer;
  }

  #showProgress(received, total) {
    const megabytes = (bytes) => (bytes / 1024 / 1024).toFixed(1);

    if (!total) {
      this.#showStatus(format(this.downloadingText, megabytes(received)));
      return;
    }

    const percent = Math.round((received / total) * 100);
    this.#status.hidden = false;
    this.#status.innerHTML = `
      <p class="mb-2"></p>
      <div class="progress" role="progressbar" aria-valuenow="${percent}" aria-valuemin="0" aria-valuemax="100">
        <div class="progress-bar" style="width: ${percent}%"></div>
      </div>`;
    this.#status.querySelector('p').textContent = format(
      this.downloadingOfText,
      megabytes(received),
      megabytes(total)
    );
    this.#status.querySelector('.progress').setAttribute('aria-label', this.downloadProgressText);
  }

  #showStatus(message, variant = null) {
    this.#status.hidden = false;
    this.#status.replaceChildren(Object.assign(document.createElement('p'), {
      className: variant ? `alert alert-${variant} mb-0` : 'mb-0',
      textContent: message
    }));
  }

  /**
   * Keyboard navigation between slides
   * @param {KeyboardEvent} e Keyboard event
   */
  #navigate(e) {
    if (!this.#previewer?.pptx) {
      return;
    }

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
      case 'PageDown':
      case ' ':
        this.#previewer.renderNextSlide();
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
      case 'PageUp':
        this.#previewer.renderPreSlide();
        break;
      case 'Home':
        this.#previewer.renderSingleSlide(0);
        this.#previewer.updatePagination();
        break;
      case 'End':
        this.#previewer.renderSingleSlide(this.#previewer.slideCount - 1);
        this.#previewer.updatePagination();
        break;
      default:
        return;
    }

    e.preventDefault();
  }
}
